import { useEffect, useRef, type RefObject } from 'react';

import { AudioEngine, ClientLogicRuntime, useLiveSceneStore } from '../../engine';
import type { HollowScriptRuntime, HollowScriptRuntimeHandles } from '../../engine/scripting/hollowScriptRuntime';
import { buildScriptModules } from '../../engine/scripting/scriptModules';
import { narrowScriptCapabilities } from './editor-panels';
import { useScriptConsoleStore } from './scriptConsoleStore';
import { listScripts, type ScriptDocument } from '../services/scripts';

/**
 * Extracted from `PreviewRoute`'s two inline `useEffect` blocks (originally
 * `screens-editor.tsx` lines ~2668-2699) so the handle-construction and
 * capability-sync logic - both real, security-relevant work (this is what
 * decides which live engine objects a HollowScript run can reach, and which
 * `uses` capabilities it is granted) - has automated coverage instead of only
 * "manually verified live in the browser". This repo has no
 * `@testing-library/react` and no full-component-render test precedent; the
 * existing precedent (`narrowScriptCapabilities` in `editor-panels.tsx`) is
 * to keep the logic in small, pure, directly-testable functions instead. The
 * three functions below follow that: each is callable and assertable with no
 * React rendering at all (`tests/unit/usePreviewScriptRuntime.test.ts`).
 * `usePreviewScriptRuntime` itself is the only part left React-shaped - two
 * one-line `useEffect` bodies that just call into the tested functions - and
 * is what `PreviewRoute` now calls instead of duplicating any of this.
 */

export interface PreviewScriptRuntimeRefs {
  readonly scriptLogicRef: RefObject<ClientLogicRuntime | null>;
  readonly audioEngineRef: RefObject<AudioEngine | null>;
}

/** Builds the live `HollowScriptRuntimeHandles` a Preview session's scripts
 * run against, reading through the refs at call time (not at construction
 * time) - the same "cheap closures over refs populated later" shape the
 * inline version relied on, since `GameRuntime`/`AudioEngine` mount after
 * this runs. `useLiveSceneStore.getState()` is a plain vanilla-store read,
 * not a React hook call, so this function itself is a pure `(refs) => handles`
 * mapping, safe to call from anywhere, including a test with no React
 * renderer at all. */
export function buildPreviewScriptHandles(refs: PreviewScriptRuntimeRefs): HollowScriptRuntimeHandles {
  const { scriptLogicRef, audioEngineRef } = refs;
  return {
    liveScene: useLiveSceneStore.getState(),
    logic: {
      variable: (name) => scriptLogicRef.current?.variable(name) ?? 0,
      setVariable: (name, value) => {
        scriptLogicRef.current?.setVariable(name, value);
      },
      applyAction: (action) => {
        scriptLogicRef.current?.applyAction(action);
      },
    },
    audio: {
      playOneShot: (url, category) => audioEngineRef.current?.playOneShot(url, category) ?? Promise.resolve(false),
    },
    // `print` from a script the session runs on its own (a run-on-start
    // script) lands in the console's scrollback via the store; a console-
    // typed run supplies its own `onPrint` and never reaches this.
    output: {
      writeLine: (text) => {
        useScriptConsoleStore.getState().appendSessionOutput(text);
      },
    },
  };
}

export type HollowScriptRuntimeCtor = new (handles: HollowScriptRuntimeHandles) => HollowScriptRuntime;

export interface AttachPreviewScriptRuntimeDeps {
  /** Defaults to the real dynamic import of `hollowScriptRuntime.ts` -
   * @hollow/script and its Worker are only fetched once a Preview session
   * actually starts, not on every app load. Overridable so a test can supply
   * a fake constructor and assert on it without ever touching a real
   * `Worker` (jsdom has none). */
  readonly loadRuntimeCtor: () => Promise<HollowScriptRuntimeCtor>;
}

function defaultLoadRuntimeCtor(): Promise<HollowScriptRuntimeCtor> {
  return import('../../engine/scripting/hollowScriptRuntime').then((m) => m.HollowScriptRuntime);
}

/**
 * Runs every `runOnStart` script in the project's Code workspace roster
 * once, against the just-constructed live runtime - the "first-class code
 * editor" pillar's actual payoff (docs/editor-consolidation roadmap follow-
 * on, Phase G): authored HollowScript can be genuine gameplay code, not only
 * something typed ad hoc into a console.
 *
 * **v1 scope, deliberately**: fires once per Preview mount, with no
 * once-per-session guard against a host migration or reconnect re-running
 * this - `clientLogicRuntime.ts`'s `round-start` handling has that exact
 * discipline for authored Logic rules, and boot scripts don't share it yet.
 * A boot script that calls a `logic.*` capability with a side effect (e.g.
 * `logic.applyAction`) could therefore double-apply across a host handoff.
 * This is a known, accepted v1 gap, not an oversight - multiplayer-safety
 * parity with Logic rules is real follow-on work, tracked separately rather
 * than blocking the first working version of "code that runs the game" on
 * it. Errors surface to the console (the same place a manually-run script's
 * errors already go) rather than being silently swallowed or freezing
 * anything - a runaway/broken boot script must never take Preview down with
 * it.
 */
export function attachBootScripts(
  runtime: HollowScriptRuntime,
  projectId: string,
  allowedCapabilities: readonly string[] | undefined,
  loadScripts: (projectId: string) => Promise<ScriptDocument[]>,
): () => void {
  let cancelled = false;
  void loadScripts(projectId)
    .then((scripts) => {
      if (cancelled) return;
      const capabilities = narrowScriptCapabilities(allowedCapabilities);
      for (const script of scripts) {
        if (!script.runOnStart) continue;
        // Every sibling script is importable from a boot script, the same
        // way it is from a Code-workspace run (`codeRunStore.ts`).
        const modules = buildScriptModules(scripts, script.name);
        void runtime.runScript(script.source, { capabilities, modules }).then((outcome) => {
          if (!outcome.ok) {
            console.error(`[boot-script] "${script.name}" failed to run on start:`, outcome.diagnostics);
          }
        });
      }
    })
    .catch((error: unknown) => {
      console.error('[boot-script] failed to load the project\'s scripts for run-on-start:', error);
    });
  return () => {
    cancelled = true;
  };
}

/** Where `attachBootScriptsWhenReady` gets the live runtime from - injected
 * so a test can drive it without a real zustand store. Defaults to
 * `scriptConsoleStore`, the same one `attachPreviewScriptRuntime` registers
 * into. */
export interface RuntimeSource {
  getRuntime: () => HollowScriptRuntime | null;
  subscribe: (listener: (runtime: HollowScriptRuntime | null) => void) => () => void;
}

const defaultRuntimeSource: RuntimeSource = {
  getRuntime: () => useScriptConsoleStore.getState().runtime,
  subscribe: (listener) => useScriptConsoleStore.subscribe((state) => listener(state.runtime)),
};

/**
 * Fires `attachBootScripts` the moment a live runtime exists, whether it is
 * already there (`getRuntime()` returns non-null immediately) or arrives
 * later (subscribes and waits) - `attachPreviewScriptRuntime`'s own runtime
 * construction is a separate async dynamic import, so which happens first
 * (this call, or that import resolving) isn't guaranteed. Fires at most
 * once per call, guarded by `detach` already being set. The caller
 * (`usePreviewScriptRuntime`) only calls this once the project's real
 * capability grant has actually loaded - see that hook's own comment for
 * why firing against a not-yet-loaded grant was a real bug, not a
 * theoretical one.
 */
export function attachBootScriptsWhenReady(
  projectId: string,
  allowedCapabilities: readonly string[] | undefined,
  loadScripts: (projectId: string) => Promise<ScriptDocument[]>,
  runtimeSource: RuntimeSource = defaultRuntimeSource,
): () => void {
  let cancelled = false;
  let detach: (() => void) | null = null;
  const tryAttach = (runtime: HollowScriptRuntime | null) => {
    if (cancelled || !runtime || detach) return;
    detach = attachBootScripts(runtime, projectId, allowedCapabilities, loadScripts);
  };
  tryAttach(runtimeSource.getRuntime());
  const unsubscribe = runtimeSource.subscribe(tryAttach);
  return () => {
    cancelled = true;
    unsubscribe();
    detach?.();
  };
}

/**
 * Constructs a `HollowScriptRuntime` from `refs` and registers it into
 * `scriptConsoleStore` once the dynamic import resolves, so `DockConsole`
 * (rendered outside `PreviewRoute`, in the shared editor chrome) can reach
 * it. Returns a cleanup function: disposes the runtime if it was already
 * constructed, clears the store either way, and guards against the import
 * resolving after the caller has already gone away (`cancelled`) - the exact
 * behaviour the inline effect had, now a plain `(refs) => cleanup` function
 * a test can call directly without mounting anything. Boot scripts are
 * *not* fired from here - see `attachBootScriptsWhenReady`, called
 * separately once the project's capability grant has actually loaded.
 */
export function attachPreviewScriptRuntime(
  refs: PreviewScriptRuntimeRefs,
  deps: AttachPreviewScriptRuntimeDeps = { loadRuntimeCtor: defaultLoadRuntimeCtor },
): () => void {
  let cancelled = false;
  let runtime: HollowScriptRuntime | null = null;
  void deps.loadRuntimeCtor().then((RuntimeCtor) => {
    if (cancelled) return;
    runtime = new RuntimeCtor(buildPreviewScriptHandles(refs));
    useScriptConsoleStore.getState().setRuntime(runtime);
  });
  return () => {
    cancelled = true;
    runtime?.dispose();
    useScriptConsoleStore.getState().setRuntime(null);
  };
}

/**
 * Syncs a project's declared capability grant (task item 4,
 * `GameSetupDoc.scriptCapabilities`) into `scriptConsoleStore` so a creator
 * unchecking a capability in Game Setup's "Scripting capabilities" panel
 * takes effect on the current Preview session's very next script run.
 * Returns the cleanup that clears it back to "not configured" - a plain
 * `(names) => cleanup` function, directly testable.
 */
export function syncPreviewScriptCapabilities(scriptCapabilities: readonly string[] | undefined): () => void {
  useScriptConsoleStore.getState().setAllowedCapabilities(scriptCapabilities);
  return () => useScriptConsoleStore.getState().setAllowedCapabilities(undefined);
}

/**
 * `PreviewRoute`'s scripting wiring: constructs and registers this session's
 * `HollowScriptRuntime` on mount (disposing it on unmount), keeps
 * `scriptConsoleStore`'s `allowedCapabilities` in sync with the project's own
 * declared grant, and runs the project's `runOnStart` scripts once the real
 * grant has actually loaded (`scriptCapabilitiesLoaded`).
 *
 * That last condition matters and is not cosmetic: `scriptCapabilities`
 * comes from `useModeDocument('gamesetup', GAMESETUP_DOC_FALLBACK)`, which
 * renders with the fallback (no `scriptCapabilities` field at all, i.e.
 * `undefined` - the "grant every v1 capability" sentinel) before the real
 * document has loaded. Firing boot scripts against that captured-at-mount
 * value - the previous version of this hook did, via a `[projectId]`-only
 * effect - meant a `runOnStart` script always ran with full access
 * regardless of what the project actually grants, every single Preview
 * open, not as a rare race. `attachBootScriptsWhenReady` is deliberately a
 * separate subscription rather than a dependency on `attachPreviewScriptRuntime`
 * itself, since the runtime's own construction (a second, independent async
 * dynamic import) and the capability grant's load are two unrelated races -
 * whichever finishes last is the one boot scripts actually wait on.
 */
export function usePreviewScriptRuntime(
  refs: PreviewScriptRuntimeRefs,
  projectId: string,
  scriptCapabilities: readonly string[] | undefined,
  scriptCapabilitiesLoaded: boolean,
): void {
  const scriptCapabilitiesRef = useRef(scriptCapabilities);
  scriptCapabilitiesRef.current = scriptCapabilities;

  // Deliberately `[projectId]`: `refs` holds `useRef` objects, stable for
  // the component's lifetime, and the runtime itself must be constructed
  // exactly once per Preview session - re-running this on every render
  // would tear down and rebuild it (dropping DockConsole's reference to it)
  // far more often than intended. A project switch (a real navigation to a
  // different project's own Preview) is the one thing that should re-run
  // this.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => attachPreviewScriptRuntime(refs), [projectId]);
  useEffect(() => syncPreviewScriptCapabilities(scriptCapabilities), [scriptCapabilities]);
  useEffect(() => {
    if (!scriptCapabilitiesLoaded) return;
    // Reads the ref rather than closing over `scriptCapabilities` directly:
    // this effect must fire exactly once per Preview session the moment
    // loading completes, not re-fire (and re-run every boot script again)
    // on every later capability-grant edit - `scriptCapabilitiesRef.current`
    // is already the real, loaded value by the time this runs, since
    // renders commit before effects.
    return attachBootScriptsWhenReady(projectId, scriptCapabilitiesRef.current, listScripts);
  }, [projectId, scriptCapabilitiesLoaded]);
}
